/**
 * Terminal Component
 *
 * React component that renders an xterm.js terminal and connects to the
 * terminal server via WebSocket. Handles resize automatically using
 * ResizeObserver and FitAddon.
 */

import { FitAddon } from "@xterm/addon-fit";
import { Terminal as XTerm } from "@xterm/xterm";
import { useCallback, useEffect, useRef, useState } from "react";
import type { TerminalProps } from "./types";
import { useTerminal } from "./useTerminal";

// Import xterm.js CSS in consumers - we don't bundle it here
// import "@xterm/xterm/css/xterm.css";

export function Terminal({
  url,
  terminalId,
  createOptions,
  upsert,
  onData,
  onExit,
  onConnectionChange,
  onSession,
  onError,
  autoReconnect = true,
  theme,
  fontFamily = "monospace",
  fontSize = 14,
  className,
  style,
}: TerminalProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [terminal, setTerminal] = useState<XTerm | null>(null);
  const [fitAddon, setFitAddon] = useState<FitAddon | null>(null);
  const resizeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Use the terminal connection hook
  const { connectionState, resize } = useTerminal({
    url,
    terminalId,
    createOptions,
    upsert,
    terminal,
    fitAddon,
    onData,
    onExit,
    onSession,
    onError,
    autoReconnect,
  });

  // Notify parent of connection state changes
  useEffect(() => {
    onConnectionChange?.(connectionState);
  }, [connectionState, onConnectionChange]);

  // Initialize xterm.js
  useEffect(() => {
    if (!containerRef.current) return;

    const term = new XTerm({
      fontFamily,
      fontSize,
      theme,
      cursorBlink: true,
      allowProposedApi: true,
    });

    const fit = new FitAddon();
    term.loadAddon(fit);

    term.open(containerRef.current);

    // Initial fit
    requestAnimationFrame(() => {
      fit.fit();
    });

    setTerminal(term);
    setFitAddon(fit);

    return () => {
      term.dispose();
      setTerminal(null);
      setFitAddon(null);
    };
  }, [fontFamily, fontSize, theme]);

  // Handle resize - fit immediately, debounce server notification
  const handleResize = useCallback(() => {
    if (!fitAddon || !terminal) return;

    // Fit immediately for visual update
    fitAddon.fit();

    // Clear existing timeout
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }

    // Debounce server notification to avoid excessive messages
    resizeTimeoutRef.current = setTimeout(() => {
      resizeTimeoutRef.current = null;
      const dims = fitAddon.proposeDimensions();
      if (dims) {
        resize({ cols: dims.cols, rows: dims.rows });
      }
    }, 50);
  }, [fitAddon, terminal, resize]);

  // Set up ResizeObserver
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !fitAddon) return;

    const resizeObserver = new ResizeObserver(() => {
      handleResize();
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [fitAddon, handleResize]);

  return <div ref={containerRef} className={className} style={style} />;
}
